import { EmailTemplate } from "@/types/EmailTemplate";
import { SenderAddress } from "@/types/SenderAddress";
import { EmailDomain } from "@/types/EmailDomain";
import {
  baseOf,
  resolveLangKey,
  resolveLangKeyFromMap,
} from "@/lib/utils/resolve-lang";

export type TemplateData = {
  html: Record<string, string>;
  emailTemplateId: string;
  languages: string[];
  /**
   * NOT the template's primary language in the single-guest dialog — see
   * `buildTemplateData`, which overwrites this with the language MATCHED for the
   * guest so the preview and the test mail default to it. Anything that needs
   * the template's actual primary (the fallback the worker will use) must read
   * `truePrimaryLanguage`.
   */
  primaryLanguage: string;
  /** The template's declared `primaryLanguage`, always, in every lane. */
  truePrimaryLanguage: string;
  templateName: string;
  replyToMail: string;
  bccMail: string;
  mailSubject: Record<string, string>;
  contentAreaBackgroundColor: string;
  /** The template's OWN sender id (`senderMail`), or "" — NOT a resolved address. */
  emailId: string;
  /** "Send as the platform default." Mutually exclusive with `emailId`. */
  platformSenderEnabled: boolean;
  emailDomainId: string;
  domainName: string;
};

/**
 * Resolve the DOMAIN facts the send payload carries alongside the sender id.
 *
 * This used to also build a `senderName` display string, ungated: it printed
 * the alias of a sender whose domain had gone unverified (the worker drops
 * those), and when the domain wasn't loaded at all it printed a HALF address
 * ("HeidiFest <info>"). Nothing composes a From string here any more — the
 * surfaces resolve it at render time through `resolveEffectiveSender`, which is
 * the only place that knows the full three-rung chain.
 */
function resolveSenderDomain(
  senderMailId: string | undefined,
  senderAddresses: SenderAddress[],
  domains: EmailDomain[],
): { emailDomainId: string; domainName: string } {
  const senderAddress = senderAddresses.find(
    (addr) => addr.senderAddressId === senderMailId,
  );
  if (!senderAddress) return { emailDomainId: "", domainName: "" };

  const domain = domains.find(
    (d) => d.emailDomainId === senderAddress.emailDomainId,
  );

  return {
    emailDomainId: senderAddress.emailDomainId,
    domainName: domain?.domain ?? "",
  };
}

export function buildTemplateData(
  template: EmailTemplate,
  senderAddresses: SenderAddress[],
  domains: EmailDomain[],
  guestLanguage?: string,
): TemplateData {
  // A platform-pinned template names no sender domain at all. Defence in depth:
  // by backend invariant such a template has no `senderMail`, so this is only
  // reachable via a corrupt row — but the consumer FAILS THE WHOLE JOB on an
  // unverified `emailDomainId`, and a job killed over a domain the send was
  // never going to use would be a spectacularly confusing way to find out.
  const platformSenderEnabled = template.platformSenderEnabled ?? false;
  const { emailDomainId, domainName } = platformSenderEnabled
    ? { emailDomainId: "", domainName: "" }
    : resolveSenderDomain(template.senderMail, senderAddresses, domains);

  const allLanguages = [template.primaryLanguage, ...(template.languages ?? [])];
  // Tolerant match: guest "de-DE" resolves a template keyed "de" (and vice
  // versa) instead of silently falling back to the primary language.
  const matchedLanguage = resolveLangKey(
    allLanguages,
    guestLanguage,
    template.primaryLanguage,
  );

  const otherLanguages = allLanguages.filter((l) => l !== matchedLanguage);

  return {
    html: template.html ?? {},
    emailTemplateId: template.emailTemplateId ?? "",
    languages: otherLanguages,
    primaryLanguage: matchedLanguage,
    truePrimaryLanguage: template.primaryLanguage ?? "",
    templateName: template.templateName ?? "",
    replyToMail: template.replyToMail ?? "",
    bccMail: template.bccMail ?? "",
    mailSubject: template.mailSubject ?? {},
    contentAreaBackgroundColor: template.contentAreaBackgroundColor ?? "",
    emailId: platformSenderEnabled ? "" : (template.senderMail ?? ""),
    platformSenderEnabled,
    emailDomainId,
    domainName,
  };
}

/**
 * Where a recipient's render language came from — the four states the send
 * dialog is entitled to state out loud.
 */
export type RecipientLanguageState =
  /** The recipient's own language, with content of its own. */
  | "own"
  /** No language on the guest record → they get the template's fallback. */
  | "fallback-no-language"
  /** The guest HAS a language; the template speaks no variant of it. */
  | "fallback-no-variant"
  /** Their own language exists as a key, but its content is byte-identical to
   *  the primary language's — i.e. the variant was declared and never written,
   *  and the recipient receives the primary language's prose under it. */
  | "identical";

/** The language ONE recipient will actually be rendered in, and where it came from. */
export type RecipientLanguage = {
  /** The content-map key the body resolves to, or null when there is no content
   *  at all (the worker then sends its "No content available" placeholder — and
   *  naming a language would be the most confident lie available). */
  code: string | null;
  state: RecipientLanguageState;
  /**
   * For `identical` only: the key whose content this one matched. The chip names
   * it in its sentence, and it is NOT necessarily the template's declared
   * primary — a template can hold no content for its own primary language, and
   * a sentence naming a language the comparison never touched is exactly the
   * kind of confident falsehood this whole component exists to remove.
   */
  comparedTo?: string;
};

/**
 * Two bakes of the same craft tree differ in the `lang="…"` attribute on `<html>`
 * (`parse-email.ts:860`) even when nothing has been translated — that is language
 * plumbing, not content, so it is normalised away before comparing.
 *
 * The `{{dataTable:<base64>}}` markers are deliberately LEFT ALONE, although their
 * catalog column headers are also filled from the render language
 * (`parse-email.ts:2656`). Collapsing them was tried and withdrawn: a table with
 * `source: "custom"` carries real, per-language translated prose inside that same
 * opaque blob (`:2611-2668`), so the collapse made the chip announce "identical to
 * German" over versions that were genuinely translated — the exact inverse of the
 * false all-clear the `identical` state exists to remove.
 *
 * The rule that settles it: **warn only when we are sure.** If two versions differ
 * only inside a marker, we cannot tell translation from plumbing, so the chip says
 * nothing. The price is a missed warning on templates whose sole difference is a
 * catalog table's auto-filled headers. The alternative price was contradicting a
 * real translation, and a warning that is sometimes wrong is worth less than one
 * that is rare and always right.
 */
const normaliseForComparison = (html: string): string =>
  html.replace(/\slang="[^"]*"/g, "");

/**
 * Resolve the render language for a single recipient — the same chain the worker
 * walks (`main-consumer/src/lib/email-trigger-service.ts:551-566`: guest language
 * → template fallback, over the CONTENT MAP), so the dialog can name the language
 * each recipient gets instead of leaving it to be guessed.
 *
 * Takes the html MAP, not its keys. An earlier version took the keys and could
 * therefore not see that a variant was empty, could not give the primary its own
 * base step, and picked same-base variants in storage order — three ways to name
 * a language the recipient does not get. See `resolveLangKeyFromMap`.
 *
 * `primary` must be the template's TRUE primary language
 * (`TemplateData.truePrimaryLanguage`), never the `primaryLanguage` field of
 * `TemplateData` — `buildTemplateData` above overwrites that one with the
 * guest-matched language for the single-guest dialog.
 *
 * The own-language test is a BASE-language comparison: an "en-US" guest on an
 * en-GB template gets their own language, and flagging that would send admins
 * hunting a problem that does not exist.
 */
export function resolveRecipientLanguage(
  html: Record<string, string> | undefined | null,
  guestLanguage: string | undefined | null,
  primary: string,
): RecipientLanguage {
  const code = resolveLangKeyFromMap(html, guestLanguage, primary);
  if (code === null) return { code: null, state: "fallback-no-variant" };

  const guestBase = baseOf(guestLanguage);
  if (guestBase.length === 0) return { code, state: "fallback-no-language" };
  if (baseOf(code) !== guestBase) return { code, state: "fallback-no-variant" };

  // Their own language — but does it actually say anything of its own?
  const primaryKey = resolveLangKeyFromMap(html, primary, primary);
  const own = html?.[code];
  const base = primaryKey === null ? undefined : html?.[primaryKey];
  if (
    primaryKey !== null &&
    primaryKey !== code &&
    typeof own === "string" &&
    typeof base === "string" &&
    normaliseForComparison(own) === normaliseForComparison(base)
  ) {
    return { code, state: "identical", comparedTo: primaryKey };
  }

  return { code, state: "own" };
}
